import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HidroWordmark } from "./HidroMark";
import {
  Menu, X, ChevronDown, LogIn,
  Droplets, Recycle, Gauge, CloudRain, Waves, Sprout, Activity, Wrench, Package,
  Building2, Mountain, Pickaxe, Sprout as SproutIcon, Landmark, Home as HomeIcon,
  Store, Factory, Beaker, Zap, Sun, Phone, MessageCircle, Mail,
} from "lucide-react";
import { INDUSTRIES, SOLUTIONS, BRAND } from "../lib/content";
import { SignInMenu } from "./SignInMenu";

// WhatsApp deep link (pre-filled friendly opener so the visitor doesn't stare at an empty thread).
const WA_PRETEXT = encodeURIComponent("Hi HidroVerse — I'd like to speak about a water system.");
const WA_HREF = `https://wa.me/${BRAND.whatsapp}?text=${WA_PRETEXT}`;
const TEL_HREF = `tel:${BRAND.phoneIntl}`;
const MAIL_HREF = `mailto:${BRAND.email}`;

const SOL_ICONS = {
  purification: Droplets,
  wastewater: Recycle,
  pumps: Gauge,
  harvesting: CloudRain,
  "pool-spa": Waves,
  irrigation: Sprout,
  monitoring: Activity,
  service: Wrench,
  rentals: Package,
};

const IND_ICONS = {
  lodges: Mountain,
  estates: Building2,
  "food-beverage": Factory,
  mines: Pickaxe,
  agriculture: SproutIcon,
  municipal: Landmark,
  residential: HomeIcon,
  "retail-wholesale": Store,
};

// Tool categories — mirrored from /pages/Tools.jsx GROUPS array.
// Each entry links to /tools/<id> so the hub auto-opens that tile.
const TOOL_GROUPS = [
  { id: "hydraulics",      name: "Hydraulic",         short: "Pipe sizing, friction loss, NPSH, pump head.",   Icon: Wrench  },
  { id: "water-quality",   name: "Water quality",     short: "TDS/EC, dosing, RO recovery, LSI & S&DSI.",        Icon: Beaker  },
  { id: "sizing-capacity", name: "Sizing & capacity", short: "Storage volume, rainwater harvest yield.",         Icon: Droplets },
  { id: "irrigation",      name: "Irrigation",        short: "Zoning, cycle time, pipe sizing for networks.",   Icon: Sprout  },
  { id: "pool-spa",        name: "Pool & spa",        short: "Turnover rate, circulation, pump sizing.",        Icon: Waves   },
  { id: "electrical",      name: "Electrical",        short: "Amps ↔ kW, cable sizing, plant running cost.",     Icon: Zap     },
  { id: "solar",           name: "Solar",             short: "PV array sizing for solar-powered pumps.",         Icon: Sun     },
];

export default function Nav({ forceDark = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const [mobileSignInOpen, setMobileSignInOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  const openMenu = (key) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setMenuOpen(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenuOpen(null), 180);
  };
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMenuOpen(null);
    setMobileToolsOpen(false);
    setMobileServicesOpen(false);
    setMobileIndustriesOpen(false);
  }, [location.pathname, location.hash]);

  const dark = forceDark;
  const linkColor = dark ? "text-white" : "text-[#0e0f10]";
  const navStateClass = forceDark ? (scrolled ? "dark-glass" : "over-hero") : "";

  return (
    <nav className={`hv-nav ${navStateClass}`} data-testid="site-nav">
      <div className="max-w-[1700px] mx-auto flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-home-link">
          <HidroWordmark tone={dark ? "light" : "dark"} />
        </Link>

        <div className="hidden xl:flex items-center gap-8 flex-1 justify-center">
          {/* Services — mega menu */}
          <div
            className="relative"
            onMouseEnter={() => openMenu("whatwedo")}
            onMouseLeave={scheduleClose}
          >
            <Link
              to="/services"
              className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-1`}
              data-testid="nav-whatwedo-trigger"
            >
              Services <ChevronDown size={13} />
            </Link>
            {menuOpen === "whatwedo" && (
              <div className="absolute top-full left-0 pt-4 w-[820px] z-50" data-testid="nav-whatwedo-menu">
                <div
                  className="overflow-hidden"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(24px) saturate(140%)",
                    WebkitBackdropFilter: "blur(24px) saturate(140%)",
                    border: "1px solid rgba(12,42,64,0.08)",
                    borderRadius: 14,
                    boxShadow:
                      "0 1px 2px rgba(12,42,64,0.04), 0 36px 72px -16px rgba(12,42,64,0.28)",
                  }}
                >
                  {/* Charcoal-glass header bar */}
                  <div
                    className="px-6 py-4 flex items-center justify-between"
                    style={{
                      background: "rgba(22,24,27,0.88)",
                      backdropFilter: "blur(20px) saturate(160%)",
                      WebkitBackdropFilter: "blur(20px) saturate(160%)",
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                      color: "#F5F7FA",
                    }}
                  >
                    <div>
                      <div
                        className="font-mono uppercase mb-1"
                        style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
                      >
                        Services
                      </div>
                      <div className="font-display" style={{ fontSize: "0.95rem", fontWeight: 500, color: "rgba(245,247,250,0.95)" }}>
                        Every water discipline a commercial site needs.
                      </div>
                    </div>
                    <Link
                      to="/services"
                      className="inline-flex items-center gap-2 font-mono uppercase transition-opacity duration-200 hover:opacity-80"
                      style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
                    >
                      All services <span aria-hidden>→</span>
                    </Link>
                  </div>

                  {/* Cards grid */}
                  <div className="grid grid-cols-3" style={{ background: "rgba(12,42,64,0.06)", gap: 1 }}>
                    {SOLUTIONS.map((s) => {
                      const Icon = SOL_ICONS[s.slug] || Droplets;
                      return (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="group p-4 transition-colors duration-200"
                          style={{ background: "#FFFFFF" }}
                          data-testid={`nav-whatwedo-${s.slug}`}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAF7")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className="inline-flex items-center justify-center shrink-0 mt-0.5"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 6,
                                background: "rgba(31,182,193,0.10)",
                                color: "#0C2A40",
                              }}
                            >
                              <Icon size={16} strokeWidth={1.75} />
                            </span>
                            <div>
                              <div
                                className="font-display leading-tight transition-colors"
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#0C2A40",
                                  fontWeight: 600,
                                  letterSpacing: "-0.005em",
                                }}
                              >
                                {s.name}
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Industries — mega menu */}
          <div
            className="relative"
            onMouseEnter={() => openMenu("whoweserve")}
            onMouseLeave={scheduleClose}
          >
            <Link
              to="/industries"
              className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-1`}
              data-testid="nav-whoweserve-trigger"
            >
              Industries <ChevronDown size={13} />
            </Link>
            {menuOpen === "whoweserve" && (
              <div className="absolute top-full left-0 pt-4 w-[720px] z-50" data-testid="nav-whoweserve-menu">
                <div
                  className="overflow-hidden"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(24px) saturate(140%)",
                    WebkitBackdropFilter: "blur(24px) saturate(140%)",
                    border: "1px solid rgba(12,42,64,0.08)",
                    borderRadius: 14,
                    boxShadow:
                      "0 1px 2px rgba(12,42,64,0.04), 0 36px 72px -16px rgba(12,42,64,0.28)",
                  }}
                >
                  {/* Charcoal-glass header */}
                  <div
                    className="px-6 py-4 flex items-center justify-between"
                    style={{
                      background: "rgba(22,24,27,0.88)",
                      backdropFilter: "blur(20px) saturate(160%)",
                      WebkitBackdropFilter: "blur(20px) saturate(160%)",
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                      color: "#F5F7FA",
                    }}
                  >
                    <div>
                      <div
                        className="font-mono uppercase mb-1"
                        style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
                      >
                        Industries
                      </div>
                      <div className="font-display" style={{ fontSize: "0.95rem", fontWeight: 500, color: "rgba(245,247,250,0.95)" }}>
                        One playbook per site.
                      </div>
                    </div>
                    <Link
                      to="/industries"
                      className="inline-flex items-center gap-2 font-mono uppercase transition-opacity duration-200 hover:opacity-80"
                      style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
                    >
                      All industries <span aria-hidden>→</span>
                    </Link>
                  </div>

                  {/* Cards grid — 2-col with short description */}
                  <div className="grid grid-cols-2" style={{ background: "rgba(12,42,64,0.06)", gap: 1 }}>
                    {INDUSTRIES.map((i) => {
                      const Icon = IND_ICONS[i.slug] || Mountain;
                      return (
                        <Link
                          key={i.slug}
                          to={`/industries/${i.slug}`}
                          className="group p-5 transition-colors duration-200"
                          style={{ background: "#FFFFFF" }}
                          data-testid={`nav-whoweserve-${i.slug}`}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAF7")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className="inline-flex items-center justify-center shrink-0 mt-0.5"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 6,
                                background: "rgba(31,182,193,0.10)",
                                color: "#0C2A40",
                              }}
                            >
                              <Icon size={16} strokeWidth={1.75} />
                            </span>
                            <div>
                              <div
                                className="font-display leading-tight"
                                style={{
                                  fontSize: "0.92rem",
                                  color: "#0C2A40",
                                  fontWeight: 600,
                                  letterSpacing: "-0.005em",
                                }}
                              >
                                {i.name}
                              </div>
                              <div
                                className="mt-1 font-light leading-snug"
                                style={{ fontSize: "0.78rem", color: "rgba(26,31,37,0.7)" }}
                              >
                                {i.short}
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                    {/* Fill the empty cell of the 2-col grid with a clear CTA */}
                    <Link
                      to="/industries"
                      className="group p-5 transition-colors duration-200 flex items-center justify-between"
                      style={{
                        background: "rgba(22,24,27,0.88)",
                        backdropFilter: "blur(20px) saturate(160%)",
                        WebkitBackdropFilter: "blur(20px) saturate(160%)",
                        color: "#F5F7FA",
                      }}
                      data-testid="nav-whoweserve-all"
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(12,14,16,0.94)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(22,24,27,0.88)")}
                    >
                      <div>
                        <div
                          className="font-mono uppercase mb-1"
                          style={{ fontSize: "0.6rem", letterSpacing: "0.24em", color: "#1FB6C1", fontWeight: 700 }}
                        >
                          See all
                        </div>
                        <div
                          className="font-display leading-tight"
                          style={{ fontSize: "0.95rem", fontWeight: 600, color: "rgba(245,247,250,0.95)" }}
                        >
                          Every industry we engineer for.
                        </div>
                      </div>
                      <span
                        aria-hidden
                        className="transition-transform duration-300 group-hover:translate-x-1"
                        style={{ color: "#1FB6C1", fontWeight: 700 }}
                      >
                        →
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Link to="/projects" className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold`} data-testid="nav-link-projects">Portfolio</Link>
          <Link to="/compliance" className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold`} data-testid="nav-link-compliance">Compliance</Link>
          <Link to="/insights" className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold`} data-testid="nav-link-insights">Insights</Link>

          {/* Tools — mega menu */}
          <div
            className="relative"
            onMouseEnter={() => openMenu("tools")}
            onMouseLeave={scheduleClose}
          >
            <Link
              to="/tools"
              className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-1`}
              data-testid="nav-link-tools"
            >
              Tools <ChevronDown size={13} />
            </Link>
            {menuOpen === "tools" && (
              <div className="absolute top-full right-0 pt-4 w-[820px] z-50" data-testid="nav-tools-menu">
                <div
                  className="overflow-hidden"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(24px) saturate(140%)",
                    WebkitBackdropFilter: "blur(24px) saturate(140%)",
                    border: "1px solid rgba(12,42,64,0.08)",
                    borderRadius: 14,
                    boxShadow:
                      "0 1px 2px rgba(12,42,64,0.04), 0 36px 72px -16px rgba(12,42,64,0.28)",
                  }}
                >
                  {/* Charcoal-glass header bar */}
                  <div
                    className="px-6 py-4 flex items-center justify-between"
                    style={{
                      background: "rgba(22,24,27,0.88)",
                      backdropFilter: "blur(20px) saturate(160%)",
                      WebkitBackdropFilter: "blur(20px) saturate(160%)",
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                      color: "#F5F7FA",
                    }}
                  >
                    <div>
                      <div
                        className="font-mono uppercase mb-1"
                        style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
                      >
                        Tools
                      </div>
                      <div className="font-display" style={{ fontSize: "0.95rem", fontWeight: 500, color: "rgba(245,247,250,0.95)" }}>
                        Industry-standard calculators. Real formulas, real results.
                      </div>
                    </div>
                    <Link
                      to="/tools"
                      className="inline-flex items-center gap-2 font-mono uppercase transition-opacity duration-200 hover:opacity-80"
                      style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
                      data-testid="nav-tools-all"
                    >
                      All tools <span aria-hidden>→</span>
                    </Link>
                  </div>

                  {/* Cards grid — 3-col with short blurb */}
                  <div className="grid grid-cols-3" style={{ background: "rgba(12,42,64,0.06)", gap: 1 }}>
                    {TOOL_GROUPS.map((g) => {
                      const Icon = g.Icon;
                      return (
                        <Link
                          key={g.id}
                          to={`/tools/${g.id}`}
                          className="group p-4 transition-colors duration-200"
                          style={{ background: "#FFFFFF" }}
                          data-testid={`nav-tools-${g.id}`}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAF7")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className="inline-flex items-center justify-center shrink-0 mt-0.5"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 6,
                                background: "rgba(31,182,193,0.10)",
                                color: "#0C2A40",
                              }}
                            >
                              <Icon size={16} strokeWidth={1.75} />
                            </span>
                            <div>
                              <div
                                className="font-display leading-tight"
                                style={{
                                  fontSize: "0.88rem",
                                  color: "#0C2A40",
                                  fontWeight: 600,
                                  letterSpacing: "-0.005em",
                                }}
                              >
                                {g.name}
                              </div>
                              <div
                                className="mt-1 font-light leading-snug"
                                style={{ fontSize: "0.74rem", color: "rgba(26,31,37,0.7)" }}
                              >
                                {g.short}
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Link to="/team" className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold`} data-testid="nav-link-team">Team</Link>
          <Link to="/portal" className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold`} data-testid="nav-link-portal">Customer Portal</Link>
          <Link to="/contact" className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold`} data-testid="nav-link-contact" data-testid-alias="nav-quote-cta">Contact</Link>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <SignInMenu dark={dark} />

          {/* Contact methods — replaces the floating bottom-right dock.
              Small inline-icon group that keeps the nav uncluttered while
              giving instant access to phone + WhatsApp from any page. */}
          <div className={`hidden xl:flex items-center gap-1 ${dark ? "text-white/85" : "text-[#0e0f10]"}`}>
            <a
              href={TEL_HREF}
              aria-label={`Call HidroVerse on ${BRAND.phone}`}
              title={`Call ${BRAND.phone}`}
              data-testid="nav-tel"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                width: 34, height: 34, borderRadius: 999,
                border: `1px solid ${dark ? "rgba(255,255,255,0.18)" : "rgba(12,42,64,0.16)"}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1FB6C1"; e.currentTarget.style.color = "#1FB6C1"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = dark ? "rgba(255,255,255,0.18)" : "rgba(12,42,64,0.16)"; e.currentTarget.style.color = ""; }}
            >
              <Phone size={15} strokeWidth={1.75} />
            </a>
            <a
              href={WA_HREF}
              target="_blank"
              rel="noreferrer"
              aria-label="Message HidroVerse on WhatsApp"
              title={`WhatsApp ${BRAND.phone}`}
              data-testid="nav-wa"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                width: 34, height: 34, borderRadius: 999,
                border: `1px solid ${dark ? "rgba(255,255,255,0.18)" : "rgba(12,42,64,0.16)"}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1FB6C1"; e.currentTarget.style.color = "#1FB6C1"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = dark ? "rgba(255,255,255,0.18)" : "rgba(12,42,64,0.16)"; e.currentTarget.style.color = ""; }}
            >
              <MessageCircle size={15} strokeWidth={1.75} />
            </a>
          </div>

          <button
            className={`xl:hidden w-10 h-10 inline-flex items-center justify-center ${linkColor}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="xl:hidden mt-5 border-t pt-5 pb-5 px-5 -mx-2 flex flex-col gap-3 max-h-[80vh] overflow-y-auto rounded-md"
          style={{
            borderColor: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
            background: dark ? "#0C1116" : "#FFFFFF",
            boxShadow: dark
              ? "0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset"
              : "0 24px 60px rgba(12,42,64,0.18)",
          }}
          data-testid="nav-mobile-panel"
        >
          {/* Services — collapsible */}
          <div className="flex items-center justify-between">
            <Link to="/services" className={`${linkColor} eyebrow`} data-testid="nav-mobile-services">Services</Link>
            <button
              type="button"
              onClick={() => setMobileServicesOpen((v) => !v)}
              aria-label={mobileServicesOpen ? "Hide service categories" : "Show service categories"}
              aria-expanded={mobileServicesOpen}
              data-testid="nav-mobile-services-toggle"
              className={`${linkColor} inline-flex items-center justify-center -mr-2`}
              style={{ width: 36, height: 36 }}
            >
              <ChevronDown
                size={18}
                style={{
                  transform: mobileServicesOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms ease",
                }}
              />
            </button>
          </div>
          {mobileServicesOpen && (
            <div className="pl-3 flex flex-col gap-2 -mt-1" data-testid="nav-mobile-services-sub">
              {SOLUTIONS.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className={`${linkColor} font-light`}
                  style={{ fontSize: "0.82rem", opacity: 0.78 }}
                  data-testid={`nav-mobile-services-${s.slug}`}
                >
                  {s.name}
                </Link>
              ))}
            </div>
          )}

          {/* Industries — collapsible */}
          <div className="flex items-center justify-between">
            <Link to="/industries" className={`${linkColor} eyebrow`} data-testid="nav-mobile-industries">Industries</Link>
            <button
              type="button"
              onClick={() => setMobileIndustriesOpen((v) => !v)}
              aria-label={mobileIndustriesOpen ? "Hide industry categories" : "Show industry categories"}
              aria-expanded={mobileIndustriesOpen}
              data-testid="nav-mobile-industries-toggle"
              className={`${linkColor} inline-flex items-center justify-center -mr-2`}
              style={{ width: 36, height: 36 }}
            >
              <ChevronDown
                size={18}
                style={{
                  transform: mobileIndustriesOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms ease",
                }}
              />
            </button>
          </div>
          {mobileIndustriesOpen && (
            <div className="pl-3 flex flex-col gap-2 -mt-1" data-testid="nav-mobile-industries-sub">
              {INDUSTRIES.map((i) => (
                <Link
                  key={i.slug}
                  to={`/industries/${i.slug}`}
                  className={`${linkColor} font-light`}
                  style={{ fontSize: "0.82rem", opacity: 0.78 }}
                  data-testid={`nav-mobile-industries-${i.slug}`}
                >
                  {i.name}
                </Link>
              ))}
            </div>
          )}

          <Link to="/projects" className={`${linkColor} eyebrow`}>Portfolio</Link>
          <Link to="/compliance" className={`${linkColor} eyebrow`}>Compliance</Link>
          <Link to="/insights" className={`${linkColor} eyebrow`} data-testid="nav-mobile-insights">Insights</Link>
          <div className="flex items-center justify-between">
            <Link to="/tools" className={`${linkColor} eyebrow`} data-testid="nav-mobile-tools">Tools</Link>
            <button
              type="button"
              onClick={() => setMobileToolsOpen((v) => !v)}
              aria-label={mobileToolsOpen ? "Hide tool categories" : "Show tool categories"}
              aria-expanded={mobileToolsOpen}
              data-testid="nav-mobile-tools-toggle"
              className={`${linkColor} inline-flex items-center justify-center -mr-2`}
              style={{ width: 36, height: 36 }}
            >
              <ChevronDown
                size={18}
                style={{
                  transform: mobileToolsOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms ease",
                }}
              />
            </button>
          </div>
          {mobileToolsOpen && (
            <div className="pl-3 flex flex-col gap-2 -mt-1" data-testid="nav-mobile-tools-sub">
              {TOOL_GROUPS.map((g) => (
                <Link
                  key={g.id}
                  to={`/tools/${g.id}`}
                  className={`${linkColor} font-light`}
                  style={{ fontSize: "0.82rem", opacity: 0.78 }}
                  data-testid={`nav-mobile-tools-${g.id}`}
                >
                  {g.name}
                </Link>
              ))}
            </div>
          )}
          <Link to="/team" className={`${linkColor} eyebrow`}>Team</Link>
          <Link to="/portal" className={`${linkColor} eyebrow`} data-testid="nav-mobile-portal-marketing">Customer Portal</Link>
          <Link to="/contact" className={`${linkColor} eyebrow`} data-testid="nav-mobile-contact-link">Contact</Link>

          {/* Sign in — collapsible group matching Services / Industries / Tools */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMobileSignInOpen((v) => !v)}
              aria-expanded={mobileSignInOpen}
              data-testid="nav-mobile-signin-toggle"
              className={`${linkColor} eyebrow`}
              style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setMobileSignInOpen((v) => !v)}
              aria-label={mobileSignInOpen ? "Hide sign-in options" : "Show sign-in options"}
              aria-expanded={mobileSignInOpen}
              className={`${linkColor} inline-flex items-center justify-center -mr-2`}
              style={{ width: 36, height: 36, background: "transparent", border: "none" }}
            >
              <ChevronDown
                size={18}
                style={{
                  transform: mobileSignInOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms ease",
                }}
              />
            </button>
          </div>
          {mobileSignInOpen && (
            <div className="pl-3 flex flex-col gap-2 -mt-1" data-testid="nav-mobile-signin-sub">
              <SignInMenu dark={dark} mobile />
            </div>
          )}

          {/* Direct contact methods — replaces the floating dock on mobile. */}
          <div
            className="mt-5 pt-4 flex flex-col gap-2"
            style={{ borderTop: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(12,42,64,0.08)" }}
            data-testid="nav-mobile-contact"
          >
            <div
              className="eyebrow mb-1"
              style={{
                color: dark ? "rgba(245,247,250,0.55)" : "rgba(12,42,64,0.6)",
                fontSize: "0.6rem",
              }}
            >
              Speak to HidroVerse
            </div>
            <a
              href={TEL_HREF}
              data-testid="nav-mobile-tel"
              className={`${linkColor} inline-flex items-center gap-3 py-1.5`}
            >
              <Phone size={15} strokeWidth={1.75} />
              <span style={{ fontSize: "0.92rem", fontWeight: 500 }}>{BRAND.phone}</span>
            </a>
            <a
              href={WA_HREF}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-mobile-wa"
              className={`${linkColor} inline-flex items-center gap-3 py-1.5`}
            >
              <MessageCircle size={15} strokeWidth={1.75} />
              <span style={{ fontSize: "0.92rem", fontWeight: 500 }}>WhatsApp</span>
            </a>
            <a
              href={MAIL_HREF}
              data-testid="nav-mobile-mail"
              className={`${linkColor} inline-flex items-center gap-3 py-1.5`}
            >
              <Mail size={15} strokeWidth={1.75} />
              <span style={{ fontSize: "0.92rem", fontWeight: 500 }}>{BRAND.email}</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
